// The event form, shared by create and edit.
//
// One builder for both, because every field rule below is a server invariant and
// two copies would drift the moment one of them was edited:
//
//   • The end-date field only appears for `duration`. The server force-nulls it
//     on every other type anyway, so showing it would promise something that
//     silently would not stick.
//   • The copy-override textareas are driven off the selected type's `hooks`, and
//     their placeholders are the registry defaults. That is why the registry
//     lives in @msc/shared: the form has to show the exact default the server
//     will fall back to.
//   • Changing the type re-renders the type-dependent block, so the fields always
//     match the behaviour profile that is actually selected.
//
// `method="post"` on the form is a hard rule, not decoration: if the script ever
// fails to load, the browser must not fall back to GET and put field values in
// the URL.
//
// ── Repeats ────────────────────────────────────────────────────────────────
// When `opts.recurrence` is supplied (create only, never edit) the form grows a
// "Repeats" select and submits to a DIFFERENT endpoint once it is set: a series
// is a template plus a rule, and `POST /events` refuses recurrence outright with
// RECURRENCE_UNSUPPORTED. So this is a client-side BRANCH to the series endpoint,
// not a new storage shape — migration-plan.md §5 is untouched by it.
//
// Two consequences that are easy to lose:
//   • The submit label has to change. "Create and announce" is a lie for a
//     series, whose occurrences announce later via cron Stage 0.
//   • A multi-day type cannot recur (§5.9). The two controls stay independently
//     selectable and the clash is reported, rather than one silently discarding
//     the other's choice.

import {
  HEADCOUNT_LEAD_DEFAULT_MINUTES,
  HEADCOUNT_LEAD_MAX_MINUTES,
  HEADCOUNT_LEAD_MIN_MINUTES,
  EVENT_TYPE_KEYS,
  EVENT_TYPES,
  eventType,
  eventTypeUsesEndDate,
  hookCanFire,
  type CreateEventRequest,
  type EventDetail,
  type EventTypeKey,
  type LifecycleHook,
  type SeriesRequest,
} from '@msc/shared';
import { el } from '../dom.js';
import { messageCopyFields, type MessageCopyFields } from './messageCopyFields.js';
import { placeFields } from './placeFields.js';

/** Shown under both title boxes, so the token is discoverable where it is used. */
const TITLE_HINT = 'Tip: put {day} in the title and each event gets its own weekday. "{day} Night Poker" becomes "Tuesday Night Poker".';
import { recurrenceFields } from './recurrenceFields.js';
import { tzPicker } from './tzPicker.js';

export interface EventFormOptions {
  /** Present when editing. Absent for a create. */
  event?: EventDetail;
  /** The owning group's default zone, offered as the create-time placeholder. */
  groupTimeZone: string | null;
  /**
   * Null (open/uncategorized group) offers every registry type, today's
   * behavior. Non-null narrows the picker to that subset — see docs/domain.md,
   * "Group categories". An event ALREADY on a now-disallowed type (the
   * category changed after the fact) still shows that option so editing
   * doesn't silently force a type change; see the render loop below.
   */
  allowedEventTypes?: EventTypeKey[] | null;
  submitLabel: string;
  action: string;
  onSubmit: (body: CreateEventRequest) => Promise<void>;
  /**
   * Supply this to offer "Repeats". Create only: editing an occurrence must not
   * turn it into a series, and a series' own rule is edited on the series form
   * (§5.6 — editing a series never rewrites materialized occurrences).
   */
  recurrence?: {
    /** Shown instead of `submitLabel` once a repeat is chosen. */
    seriesSubmitLabel: string;
    /** Where the form goes when it is a series. */
    seriesAction: string;
    onSubmitSeries: (body: SeriesRequest) => Promise<void>;
    /** Lets the page swap surrounding copy that only holds for one event. */
    onModeChange?: (repeating: boolean) => void;
  };
}

export function eventForm(opts: EventFormOptions): HTMLElement {
  const existing = opts.event;

  const error = el('p', { class: 'form-error', hidden: true });
  const submit = el('button', { type: 'submit', class: 'btn' }, opts.submitLabel);

  // autocorrect off, for the reason spelled out on the create form in groups.ts.
  // An event title is the same class of field: a chosen label, not prose.
  const title = input({
    id: 'ev-title',
    name: 'title',
    type: 'text',
    required: true,
    maxlength: 191,
    autocorrect: 'off',
    spellcheck: 'false',
    value: existing?.title ?? '',
  });

  const typeSelect = el('select', { id: 'ev-type', name: 'eventType' }) as HTMLSelectElement;
  const currentType = existing?.eventType ?? 'generic';
  for (const key of EVENT_TYPE_KEYS) {
    // Narrowed by the group's category, EXCEPT the type already on the event
    // being edited — always offered, so a category that changed after the
    // fact never forces an edit to silently change an unrelated field.
    if (opts.allowedEventTypes && !opts.allowedEventTypes.includes(key) && key !== currentType) {
      continue;
    }
    const def = EVENT_TYPES[key];
    const opt = el('option', { value: key }, `${def.icon} ${def.label}`) as HTMLOptionElement;
    if (key === currentType) opt.selected = true;
    typeSelect.append(opt);
  }

  const eventDate = input({
    id: 'ev-date',
    name: 'eventDate',
    type: 'date',
    required: true,
    value: existing?.eventDate ?? '',
  });
  const startTime = input({
    id: 'ev-start',
    name: 'startTime',
    type: 'time',
    value: hhmm(existing?.startTime),
  });
  const endTime = input({
    id: 'ev-end-time',
    name: 'endTime',
    type: 'time',
    value: hhmm(existing?.endTime),
  });
  const endDate = input({
    id: 'ev-end-date',
    name: 'endDate',
    type: 'date',
    value: existing?.endDate ?? '',
  });

  const location = input({
    id: 'ev-location',
    name: 'location',
    type: 'text',
    maxlength: 255,
    value: existing?.location ?? '',
  });

  const place = placeFields('ev', existing);

  const rsvpMin = input({
    id: 'ev-min',
    name: 'rsvpMin',
    type: 'number',
    min: 1,
    placeholder: 'No minimum',
    value: existing?.rsvpMin === null || existing === undefined ? '' : String(existing.rsvpMin),
  });
  const rsvpMax = input({
    id: 'ev-max',
    name: 'rsvpMax',
    type: 'number',
    min: 1,
    placeholder: 'No limit',
    value: existing?.rsvpMax === null || existing === undefined ? '' : String(existing.rsvpMax),
  });

  // Blank means the 60 minute default, stored as NULL. Bounds mirror the server
  // (shared HEADCOUNT_LEAD_*): the day-of ping is 4 hours ahead, so later than
  // that would put the headcount before the confirmations it summarises.
  const headcountLead = input({
    id: 'ev-headcount-lead',
    name: 'headcountLeadMinutes',
    type: 'number',
    min: HEADCOUNT_LEAD_MIN_MINUTES,
    max: HEADCOUNT_LEAD_MAX_MINUTES,
    placeholder: String(HEADCOUNT_LEAD_DEFAULT_MINUTES),
    value:
      existing?.headcountLeadMinutes === null || existing === undefined
        ? ''
        : String(existing.headcountLeadMinutes),
  });

  // Blank means "inherit the group's zone", stored as NULL. Only show a value
  // here when the event carries its OWN zone — echoing the inherited one back
  // would silently pin it, so a later change to the group would stop reaching
  // this event.
  //
  // Which is also why `autodetect` is deliberately absent: filling an empty
  // picker from the browser would pin a zone on every event nobody chose one
  // for. The PHP original passed it only on the create-group and signup forms,
  // never here. The inherit row names the GROUP zone, not the site zone — two
  // different chains, see shared/src/time.ts.
  const timeZone = tzPicker({
    name: 'timeZone',
    value: existing?.timeZoneIsExplicit ? existing.timeZone : '',
    inheritLabel: 'Group default',
    inheritHint: opts.groupTimeZone ?? '',
    id: 'ev-tz',
  });

  const description = textarea('ev-description', 'description', 4, existing?.description ?? '');
  const feedMessage = textarea('ev-feed-message', 'feedMessage', 2, existing?.feedMessage ?? '');

  // Rebuilt whenever the type changes.
  const typeBlock = el('div', { class: 'type-block' });
  let copy: MessageCopyFields | null = null;

  const endDateRow = el('div', { class: 'field' }, [
    el('label', { for: 'ev-end-date' }, 'End date'),
    endDate,
    el('p', { class: 'muted small' }, 'Required for a timed event. Must be after the start.'),
  ]);

  // 'Date' on a one-off is 'First occurrence' on a series — same input, and the
  // series endpoint takes it as `startDate`.
  const dateLabel = el('label', { for: 'ev-date' }, 'Date');

  // §5.9: a recurring multi-day event is not a supported shape. Both controls
  // stay selectable and the clash is named, because silently resetting either
  // one would discard a choice the organizer actually made.
  const clash = el(
    'p',
    { class: 'form-error', hidden: true },
    'A timed event cannot repeat: recurring multi-day events are not supported. ' +
      'Pick another type, or set Repeats back to “Does not repeat”.',
  );

  const recurrence = opts.recurrence
    ? recurrenceFields({ idPrefix: 'ev', allowNone: true, onChange: () => renderMode() })
    : null;

  /** True once the organizer has picked a repeat. Always false when editing. */
  function isSeries(): boolean {
    return recurrence?.isRepeating() ?? false;
  }

  function hasClash(): boolean {
    return isSeries() && eventTypeUsesEndDate(typeSelect.value);
  }

  /** Everything that differs between "one event" and "a series". */
  function renderMode(): void {
    const series = isSeries();
    submit.textContent =
      series && opts.recurrence ? opts.recurrence.seriesSubmitLabel : opts.submitLabel;
    dateLabel.textContent = series ? 'First occurrence' : 'Date';
    clash.hidden = !hasClash();
    // Keep the no-JS fallback target honest: the form posts to whichever
    // endpoint the current selection actually means.
    if (opts.recurrence) {
      form.setAttribute('action', series ? opts.recurrence.seriesAction : opts.action);
    }
    opts.recurrence?.onModeChange?.(series);
  }

  function renderTypeBlock(): void {
    const key = typeSelect.value as EventTypeKey;
    const def = eventType(key);

    endDateRow.hidden = !def.usesEndDate;
    if (!def.usesEndDate) endDate.value = '';

    const children: HTMLElement[] = [];

    const hooks = Object.entries(def.hooks) as [LifecycleHook, string][];
    copy = null;
    if (hooks.length > 0) {
      copy = messageCopyFields({
        hooks,
        existing: existing?.typeMessages,
        idPrefix: 'ev',
        named: true,
      });
      children.push(copy.element);
      syncReachableHooks();
    }

    if (def.closeMode === 'auto_at_end') {
      children.push(
        el(
          'p',
          { class: 'muted small' },
          'This type closes itself at its end time. There is no Close button.',
        ),
      );
    }
    if (def.hasResults) {
      children.push(
        el(
          'p',
          { class: 'muted small' },
          'Closing this event will ask for winners. A chop puts everyone in first place.',
        ),
      );
    }

    typeBlock.replaceChildren(...children);
  }

  /**
   * Show only the message boxes whose hook can fire on the CURRENT thresholds.
   *
   * `min_met` needs a minimum and `full` needs a maximum — without one the
   * server's `fireRsvpHooks()` skips the hook, so the box was editable
   * configuration that could never be sent. Re-run on every keystroke in either
   * threshold, so typing a maximum reveals its message immediately rather than
   * on some later save.
   */
  function syncReachableHooks(): void {
    const thresholds = {
      rsvpMin: numberOrNull(rsvpMin.value),
      rsvpMax: numberOrNull(rsvpMax.value),
    };
    copy?.setReachable((hook) => hookCanFire(typeSelect.value, hook, thresholds));
  }

  // `input`, not `change`: waiting for blur to reveal the box reads as a dead
  // control, the same reason the copy boxes themselves listen on `input`.
  rsvpMin.addEventListener('input', syncReachableHooks);
  rsvpMax.addEventListener('input', syncReachableHooks);

  typeSelect.addEventListener('change', () => {
    renderTypeBlock();
    renderMode();
  });
  renderTypeBlock();

  const form = el('form', { method: 'post', action: opts.action, novalidate: true }, [
    el('label', { for: 'ev-title' }, 'Title'),
    el('p', { class: 'muted small' }, TITLE_HINT),
    title,
    el('label', { for: 'ev-type' }, 'Event type'),
    typeSelect,
    el('p', { class: 'muted small' }, 'The type decides how the event behaves, not just how it looks.'),
    // scheduleBlock sits WITH the repeat controls, directly under "Repeats".
    // It lived below "Note to members" until 2026-09-09, which read as the
    // control being absent: the form asked when the series repeats at the top
    // and then asked when each occurrence is created two screens further down,
    // past fields that have nothing to do with recurrence. It hides itself when
    // the selection is "Does not repeat", so it only ever appears in context.
    ...(recurrence
      ? [
          recurrence.typeRow,
          recurrence.weekdayRow,
          recurrence.intervalRow,
          recurrence.scheduleBlock,
          clash,
        ]
      : []),
    dateLabel,
    eventDate,
    el('label', { for: 'ev-start' }, 'Start time'),
    startTime,
    el('p', { class: 'muted small' }, 'Leave the start time blank for an all-day event.'),
    el('label', { for: 'ev-end-time' }, 'End time'),
    endTime,
    endDateRow,
    ...(recurrence ? [recurrence.untilRow] : []),
    el('label', { for: 'ev-location' }, 'Location'),
    location,
    place.element,
    el('label', { for: 'ev-min' }, 'Minimum RSVPs'),
    rsvpMin,
    el('label', { for: 'ev-max' }, 'Maximum RSVPs'),
    rsvpMax,
    el('label', { for: 'ev-headcount-lead' }, 'Send me the headcount (minutes before start)'),
    headcountLead,
    el(
      'p',
      { class: 'muted small' },
      'Leave blank for 60. Between 15 minutes and 4 hours. You get one summary, ' +
        'so later RSVPs are not in it. An all-day event has no start time, so its ' +
        'headcount goes at 6 PM the evening before instead.',
    ),
    el('label', { for: 'ev-tz' }, 'Timezone'),
    timeZone.root,
    el(
      'p',
      { class: 'muted small' },
      'The zone the times above are IN. Group default inherits the group, and the event keeps whatever zone it was saved with.',
    ),
    el('label', { for: 'ev-description' }, 'Details'),
    description,
    el('label', { for: 'ev-feed-message' }, 'Note to members'),
    feedMessage,
    el('p', { class: 'muted small' }, 'Included in the announcement members receive.'),
    typeBlock,
    error,
    submit,
  ]) as HTMLFormElement;

  // After `form` exists: renderMode() retargets its action attribute.
  renderMode();

  /**
   * Say what is missing, right at the field, and take the person to it. The form
   * is `novalidate` (JavaScript owns the submit), so without this a missing
   * date reached the server and came back as a bare "Invalid request" at the
   * bottom of a long page, nowhere near the field that needed filling in.
   */
  function flag(field: HTMLElement, message: string): void {
    form.querySelectorAll('.field-error').forEach((n) => n.remove());
    const note = el('p', { class: 'form-error field-error', role: 'alert' }, message);
    field.insertAdjacentElement('afterend', note);
    field.setAttribute('aria-invalid', 'true');
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    (field as HTMLElement).focus({ preventScroll: true });
    const clear = (): void => {
      note.remove();
      field.removeAttribute('aria-invalid');
      field.removeEventListener('input', clear);
    };
    field.addEventListener('input', clear);
  }

  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    error.hidden = true;
    form.querySelectorAll('.field-error').forEach((n) => n.remove());

    if (title.value.trim() === '') {
      flag(title, 'A title is required.');
      return;
    }
    if (eventDate.value === '') {
      flag(eventDate, 'A date is required.');
      return;
    }

    // Refused client-side as well as by the server, because the two endpoints
    // report it differently and neither message is worth surfacing raw.
    if (hasClash()) {
      clash.hidden = false;
      return;
    }

    submit.setAttribute('disabled', 'disabled');

    // {} when the selected type has no hooks, which is the correct payload.
    const typeMessages: Record<string, string> = copy?.value() ?? {};

    const rule = recurrence?.value();
    if (rule?.repeats && opts.recurrence) {
      // The branch. Every shared field carries over unchanged; the event's
      // `endDate` deliberately does not, because on a series that column means
      // repeat-until and it comes from the rule instead.
      const series: SeriesRequest = {
        title: title.value.trim(),
        eventType: typeSelect.value,
        recurrenceType: rule.recurrenceType,
        recurrenceDays: rule.recurrenceDays,
        recurrenceInterval: rule.recurrenceInterval,
        startDate: eventDate.value,
        endDate: rule.endDate,
        startTime: startTime.value || null,
        endTime: endTime.value || null,
        location: location.value.trim(),
        ...place.value(),
        rsvpMin: numberOrNull(rsvpMin.value),
        rsvpMax: numberOrNull(rsvpMax.value),
        headcountLeadMinutes: numberOrNull(headcountLead.value),
        feedPostDaysBefore: rule.feedPostDaysBefore,
        feedPostTime: rule.feedPostTime,
        timeZone: timeZone.value(),
        description: description.value.trim(),
        feedMessage: feedMessage.value.trim(),
        typeMessages,
      };

      try {
        await opts.recurrence.onSubmitSeries(series);
      } catch (err) {
        error.textContent = err instanceof Error ? err.message : 'Something went wrong. Try again.';
        error.hidden = false;
        submit.removeAttribute('disabled');
      }
      return;
    }

    const body: CreateEventRequest = {
      title: title.value.trim(),
      eventType: typeSelect.value,
      eventDate: eventDate.value,
      startTime: startTime.value || null,
      endTime: endTime.value || null,
      endDate: endDate.value || null,
      location: location.value.trim(),
      ...place.value(),
      rsvpMin: numberOrNull(rsvpMin.value),
      rsvpMax: numberOrNull(rsvpMax.value),
      headcountLeadMinutes: numberOrNull(headcountLead.value),
      // '' is "inherit the group", which the server stores as NULL.
      timeZone: timeZone.value(),
      description: description.value.trim(),
      feedMessage: feedMessage.value.trim(),
      typeMessages,
    };

    try {
      await opts.onSubmit(body);
    } catch (err) {
      error.textContent = err instanceof Error ? err.message : 'Something went wrong. Try again.';
      error.hidden = false;
      submit.removeAttribute('disabled');
    }
  });

  return form;
}

/** A TIME column round-trips as HH:MM:SS; an <input type="time"> wants HH:MM. */
function hhmm(time: string | null | undefined): string {
  return time ? time.slice(0, 5) : '';
}

function numberOrNull(raw: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function input(attrs: Record<string, string | number | boolean>): HTMLInputElement {
  const node = el('input', attrs) as HTMLInputElement;
  // Set through the property too: `value` as an attribute is only the DEFAULT
  // value, so a re-render after a failed submit would otherwise show stale text.
  if (typeof attrs.value === 'string') node.value = attrs.value;
  return node;
}

function textarea(id: string, name: string, rows: number, value: string): HTMLTextAreaElement {
  const node = el('textarea', { id, name, rows }) as HTMLTextAreaElement;
  node.value = value;
  return node;
}
